<template>
    <div class="content-wrapper">
        <div class="row">
            <div class="col-sm-12 col-md-6">
                <app-breadcrumb :page-title="$t('attendance_dashboard')" :icon="'users'" />
            </div>
            <div class="col-sm-12 col-md-6 breadcrumb-side-button">
                <div class="float-md-right mb-3 mb-sm-3 mb-md-0">
                    <input
                        type="date"
                        v-model="selectedDate"
                        :placeholder="$t('select_date')"
                        @input="loadDashboardData"
                        class="form-control mr-2 d-inline-block"
                        style="width: auto; display: inline-block;"
                    />
                    <button 
                        type="button" 
                        class="btn btn-primary btn-with-shadow mr-2"
                        @click="loadRealTimeStats"
                        :disabled="realtimeLoader"
                    >
                        <i class="fas fa-sync-alt" :class="{ 'fa-spin': realtimeLoader }"></i>
                        <!-- {{ $t('refresh') }} -->
                    </button>
                    <button 
                        type="button" 
                        class="btn btn-success btn-with-shadow"
                        @click="exportSummary"
                    >
                        <i class="fas fa-download"></i>
                        <!-- {{ $t('export') }} -->
                    </button>
                </div>
            </div>
        </div>

        <!-- Real-time Stats Cards -->
        <div class="row mb-4" v-if="realTimeStats">
            <div class="col-lg-3 col-md-6">
                <div class="card bg-primary text-white">
                    <div class="card-body">
                        <div class="d-flex justify-content-between">
                            <div>
                                <h4 class="mb-0">{{ (realTimeStats && realTimeStats.employees_currently_present) || 0 }}</h4>
                                <p class="mb-0">{{ $t('currently_present') }}</p>
                            </div>
                            <div class="align-self-center">
                                <i class="fas fa-users fa-2x"></i>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div class="col-lg-3 col-md-6">
                <div class="card bg-success text-white">
                    <div class="card-body">
                        <div class="d-flex justify-content-between">
                            <div>
                                <h4 class="mb-0">{{ summary.attendance_rate || 0 }}%</h4>
                                <p class="mb-0">{{ $t('attendance_rate') }}</p>
                            </div>
                            <div class="align-self-center">
                                <i class="fas fa-chart-line fa-2x"></i>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div class="col-lg-3 col-md-6">
                <div class="card bg-warning text-white">
                    <div class="card-body">
                        <div class="d-flex justify-content-between">
                            <div>
                                <h4 class="mb-0">{{ summary.late_arrivals || 0 }}</h4>
                                <p class="mb-0">{{ $t('late_arrivals') }}</p>
                            </div>
                            <div class="align-self-center">
                                <i class="fas fa-clock fa-2x"></i>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div class="col-lg-3 col-md-6">
                <div class="card bg-info text-white">
                    <div class="card-body">
                        <div class="d-flex justify-content-between">
                            <div>
                                <h4 class="mb-0">{{ summary.overtime_employees || 0 }}</h4>
                                <p class="mb-0">{{ $t('overtime_employees') }}</p>
                            </div>
                            <div class="align-self-center">
                                <i class="fas fa-business-time fa-2x"></i>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Alerts Section -->
        <div class="row mb-4" v-if="dashboardData.alerts && dashboardData.alerts.length > 0">
            <div class="col-12">
                <div class="alert" 
                     v-for="alert in (dashboardData && dashboardData.alerts) || []" 
                     :key="alert.message"
                     :class="`alert-${alert.type === 'warning' ? 'warning' : 'info'}`">
                    <i class="fas fa-exclamation-triangle mr-2"></i>
                    {{ alert.message }}
                </div>
            </div>
        </div>

        <!-- Charts Row -->
        <div class="row mb-4">
            <div class="col-lg-12">
                <div class="card">
                    <div class="card-header">
                        <h5 class="card-title">{{ $t('attendance_timeline') }}</h5>
                    </div>
                    <div class="card-body">
                        <app-chart 
                            v-if="timelineChartData.labels && timelineChartData.labels.length > 0"
                            type="custom-line-chart"
                            :height="300"
                            :labels="timelineChartData.labels"
                            :data-sets="timelineChartData.datasets"
                        />
                        <div v-else class="text-center py-4">
                            <p class="text-muted">{{ $t('no_data_available') }}</p>
                        </div>
                    </div>
                </div>
            </div>
            <!-- <div class="col-lg-4">
                <div class="card">
                    <div class="card-header">
                        <h5 class="card-title">{{ $t('attendance_by_location') }}</h5>
                    </div>
                    <div class="card-body">
                        <app-chart 
                            v-if="locationChartData.labels && locationChartData.labels.length > 0"
                            type="dough-chart"
                            :height="250"
                            :labels="locationChartData.labels"
                            :data-sets="locationChartData.datasets"
                        />
                        <div v-else class="text-center py-4">
                            <p class="text-muted">{{ $t('no_data_available') }}</p>
                        </div>
                    </div>
                </div>
            </div> -->
        </div>

        <!-- Recent Activity -->
        <div class="row">
            <div class="col-lg-6">
                <div class="card">
                    <div class="card-header">
                        <h5 class="card-title">{{ $t('recent_checkins') }}</h5>
                    </div>
                    <div class="card-body">
                        <div class="list-group list-group-flush">
                            <div 
                                v-for="checkin in (realTimeStats && realTimeStats.recent_checkins) || []" 
                                :key="`checkin-${checkin.employee_name || 'unknown'}-${checkin.time || Date.now()}`"
                                class="list-group-item d-flex justify-content-between align-items-center"
                            >
                                <div>
                                    <strong>{{ checkin.employee_name || $t('unknown_employee') }}</strong><br>
                                    <small class="text-muted">{{ checkin.location || $t('unknown_location') }}</small>
                                </div>
                                <div class="text-right">
                                    <span class="badge badge-success">{{ checkin.time || '--:--' }}</span><br>
                                    <small class="text-muted">{{ checkin.method || $t('unknown_method') }}</small>
                                </div>
                            </div>
                            <div v-if="!realTimeStats.recent_checkins || realTimeStats.recent_checkins.length === 0" 
                                 class="text-center text-muted py-4">
                                {{ $t('no_recent_checkins') }}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div class="col-lg-6">
                <div class="card">
                    <div class="card-header">
                        <h5 class="card-title">{{ $t('recent_checkouts') }}</h5>
                    </div>
                    <div class="card-body">
                        <div class="list-group list-group-flush">
                            <div 
                                v-for="checkout in (realTimeStats && realTimeStats.recent_checkouts) || []" 
                                :key="`checkout-${checkout.employee_name || 'unknown'}-${checkout.time || Date.now()}`"
                                class="list-group-item d-flex justify-content-between align-items-center"
                            >
                                <div>
                                    <strong>{{ checkout.employee_name || $t('unknown_employee') }}</strong><br>
                                    <small class="text-muted">{{ checkout.location || $t('unknown_location') }}</small>
                                </div>
                                <div class="text-right">
                                    <span class="badge badge-danger">{{ checkout.time || '--:--' }}</span><br>
                                    <small class="text-muted">{{ checkout.method || $t('unknown_method') }}</small>
                                </div>
                            </div>
                            <div v-if="!realTimeStats.recent_checkouts || realTimeStats.recent_checkouts.length === 0" 
                                 class="text-center text-muted py-4">
                                {{ $t('no_recent_checkouts') }}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Detailed Employee Table -->
        <div class="row mt-4">
            <div class="col-12">
                <div class="card">
                    <div class="card-header d-flex justify-content-between align-items-center">
                        <h5 class="card-title mb-0">{{ $t('employee_details') }}</h5>
                        <button 
                            type="button" 
                            class="btn btn-outline-primary btn-sm"
                            @click="showDetailedView = !showDetailedView"
                        >
                            {{ showDetailedView ? $t('hide_details') : $t('show_details') }}
                        </button>
                    </div>
                    <div class="card-body" v-if="showDetailedView">
                        <app-table 
                            :id="employeeTableId" 
                            :options="employeeTableOptions" 
                            @action="handleEmployeeAction" 
                        />
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import CoreLibrary from '../../../../core/helpers/CoreLibrary';
import { ATTENDANCE_DASHBOARD_SUMMARY, ATTENDANCE_REAL_TIME_STATS, ATTENDANCE_EXPORT_SUMMARY } from '../../../Config/ApiUrl';

export default {
    extends: CoreLibrary,
    name: "AttendanceDashboardSummary",
    data() {
        return {
            selectedDate: window.moment ? window.moment().format('YYYY-MM-DD') : new Date().toISOString().split('T')[0],
            summary: {
                attendance_by_location: {}
            },
            realTimeStats: {
                employees_currently_present: 0,
                recent_checkins: [],
                recent_checkouts: []
            },
            dashboardData: {
                alerts: [],
                hourly_checkins: [],
                hourly_checkouts: []
            },
            employeeDetails: [],
            showDetailedView: false,
            realtimeLoader: false,
            isLoading: false,
            preLoader: false,
            employeeTableId: 'employee-attendance-table',
            timelineChartData: {
                labels: [],
                datasets: []
            },
            locationChartData: {
                labels: [],
                datasets: []
            },
        }
    },
    mounted() {
        this.loadDashboardData();
        this.loadRealTimeStats();
        // Auto-refresh every 2 minutes
        setInterval(() => {
            this.loadRealTimeStats();
        }, 120000);
    },
    beforeDestroy() {
        if (this.timelineChart) this.timelineChart.destroy();
        if (this.locationChart) this.locationChart.destroy();
    },
    methods: {
        async loadDashboardSummary() {
            try {
                this.summaryLoader = true;
                const response = await this.axiosGet(ATTENDANCE_DASHBOARD_SUMMARY);
                this.dashboardData = {
                    attendance_rate: (response.data && response.data.attendance_rate) || 0,
                    total_employees: (response.data && response.data.total_employees) || 0,
                    present_employees: (response.data && response.data.present_employees) || 0,
                    absent_employees: (response.data && response.data.absent_employees) || 0,
                    late_arrivals: (response.data && response.data.late_arrivals) || 0,
                    average_checkin_time: (response.data && response.data.average_checkin_time) || '00:00',
                    chart_data: (response.data && response.data.chart_data) || [],
                    ...(response.data || {})
                };
            } catch (error) {
                console.error('Error loading dashboard summary:', error);
                this.dashboardData = {
                    attendance_rate: 0,
                    total_employees: 0,
                    present_employees: 0,
                    absent_employees: 0,
                    late_arrivals: 0,
                    average_checkin_time: '00:00',
                    chart_data: []
                };
                if (this.$toastr) {
                    this.$toastr.e('', this.$t('error_loading_data'));
                }
            } finally {
                this.summaryLoader = false;
            }
        },

        async loadRealTimeStats() {
            try {
                this.realtimeLoader = true;
                const response = await this.axiosGet(ATTENDANCE_REAL_TIME_STATS);
                this.realTimeStats = {
                    employees_currently_present: (response.data && response.data.employees_currently_present) || 0,
                    recent_checkins: (response.data && response.data.recent_checkins) || [],
                    recent_checkouts: (response.data && response.data.recent_checkouts) || [],
                    ...(response.data || {})
                };
            } catch (error) {
                console.error('Error loading real-time stats:', error);
                this.realTimeStats = {
                    employees_currently_present: 0,
                    recent_checkins: [],
                    recent_checkouts: []
                };
                if (this.$toastr) {
                    this.$toastr.e('', this.$t('error_loading_data'));
                }
            } finally {
                this.realtimeLoader = false;
            }
        },

        createTimelineChart() {
            // Préparer les données pour le graphique timeline
            const labels = ['8:00', '9:00', '10:00', '11:00', '12:00', '13:00', '14:00', '15:00', '16:00', '17:00'];
            
            // Vérifier que dashboardData existe et utiliser les vraies données ou des données par défaut
            let checkinData = [12, 19, 3, 5, 2, 3, 10, 15, 8, 4]; // Données par défaut
            let checkoutData = [2, 5, 8, 12, 15, 18, 20, 15, 10, 5]; // Données par défaut
            
            if (this.dashboardData && this.dashboardData.hourly_checkins && Array.isArray(this.dashboardData.hourly_checkins)) {
                checkinData = this.dashboardData.hourly_checkins;
            }
            
            if (this.dashboardData && this.dashboardData.hourly_checkouts && Array.isArray(this.dashboardData.hourly_checkouts)) {
                checkoutData = this.dashboardData.hourly_checkouts;
            }
            
            this.timelineChartData = {
                labels: labels,
                datasets: [
                    {
                        label: this.$t('check_ins'),
                        data: checkinData,
                        borderColor: 'rgba(75, 192, 192, 1)',
                        backgroundColor: 'rgba(75, 192, 192, 0.2)',
                        tension: 0.1
                    },
                    {
                        label: this.$t('check_outs'),
                        data: checkoutData,
                        borderColor: 'rgba(255, 99, 132, 1)',
                        backgroundColor: 'rgba(255, 99, 132, 0.2)',
                        tension: 0.1
                    }
                ]
            };
        },

        renderLocationChart() {
            // Préparer les données pour le graphique par localisation
            if (!this.summary || !this.summary.attendance_by_location) {
                this.locationChartData = { labels: [], datasets: [] };
                return;
            }
            
            const locations = Object.keys(this.summary.attendance_by_location);
            const counts = Object.values(this.summary.attendance_by_location).map(data => data.count || data);
            
            this.locationChartData = {
                labels: locations,
                datasets: [{
                    data: counts,
                    backgroundColor: [
                        'rgba(255, 99, 132, 0.8)',
                        'rgba(54, 162, 235, 0.8)',
                        'rgba(255, 205, 86, 0.8)',
                        'rgba(75, 192, 192, 0.8)',
                        'rgba(153, 102, 255, 0.8)',
                        'rgba(255, 159, 64, 0.8)'
                    ]
                }]
            };
        },

        updateCharts() {
            this.createTimelineChart();
            this.renderLocationChart();
        },

        // Helper method for moment
        moment(date) {
            return window.moment ? window.moment(date) : new Date(date);
        },

        exportSummary() {
            const url = `${ATTENDANCE_EXPORT_SUMMARY}?date=${this.selectedDate}`;
            window.open(url, '_blank');
        },

        handleEmployeeAction(rowData, actionObj) {
            // Handle employee specific actions if needed
        }
    }
}
</script>

<style scoped>
.card {
    /* box-shadow: 0 0 20px rgba(0,0,0,0.1); */
    border: none;
    border-radius: 10px;
}

.badge {
    font-size: 0.75em;
}

.list-group-item {
    border: none;
    padding: 0.75rem 0;
    border-bottom: 1px solid #f8f9fa;
}

.list-group-item:last-child {
    border-bottom: none;
}
</style>
